/**
 * DATContractPullDialog — Modal zum manuellen Abrufen von DAT-Kalkulations-
 * Daten anhand einer Vorgangsnummer (Contract-ID).
 *
 * Use-Case: Die Kalkulation wurde extern in SilverDAT calculateExpert
 * erstellt (z.B. vom Admin oder einem Kollegen), und der Gutachter will
 * die Werte mit einem Klick in seine Akte ziehen. Der Pull-Back-Endpoint
 * extrahiert dann automatisch:
 *   • Fahrzeug-Anreicherung (Modell, Bauzeit, DAT-€-Code, Ausstattung)
 *   • Kalkulations-Summen (Lohn/Lack/Teile/Brutto)
 *   • Detail-Positionen (Ersatzteile + Arbeitslohn pro Position)
 *   • Bewertung (Wiederbeschaffungswert, Restwert)
 *   • Auto-PDF-Anhang in der Akte
 */
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Download, AlertCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export default function DATContractPullDialog({
  caseId,
  open,
  onClose,
  onPulled,
  defaultContractId = "",
}) {
  const [contractId, setContractId] = useState(defaultContractId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setContractId(defaultContractId || "");
    setError("");
    setLoading(false);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    const trimmed = String(contractId || "").trim();
    if (!trimmed) {
      setError("Bitte eine DAT-Vorgangsnummer eingeben.");
      return;
    }
    if (!/^\d+$/.test(trimmed)) {
      setError("Die Vorgangsnummer muss eine reine Zahl sein (z.B. 122575735).");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(
        `/cases/${caseId}/dat/pull-back`,
        { contract_id: Number(trimmed) },
        { timeout: 60_000 }
      );
      const parts = [];
      if (data?.equipment_count) parts.push(`${data.equipment_count} Ausstattungen`);
      if (data?.spare_parts_count) parts.push(`${data.spare_parts_count} Ersatzteile`);
      if (data?.labour_count) parts.push(`${data.labour_count} Arbeitslohn-Positionen`);
      if (data?.calculation_fields) parts.push(`${data.calculation_fields} Summen`);
      if (data?.vehicle_fields) parts.push(`${data.vehicle_fields} Fahrzeugfelder`);
      toast.success(
        parts.length > 0
          ? `DAT-Vorgang #${trimmed} importiert: ${parts.join(" · ")}`
          : `DAT-Vorgang #${trimmed} importiert`
      );
      onPulled?.(data);
      reset();
      onClose?.();
    } catch (err) {
      setError(apiError(err, "Daten konnten nicht von DAT abgerufen werden."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose?.();
        }
      }}
    >
      <DialogContent className="sm:max-w-lg" data-testid="dat-contract-pull-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5 text-cyan-600" />
            Daten aus DAT-Vorgang abrufen
          </DialogTitle>
          <DialogDescription>
            Gib die DAT-Vorgangsnummer (Contract-ID) ein. Wir holen automatisch
            Fahrzeugdaten, Kalkulations-Summen, alle Detail-Positionen und
            Bewertungswerte aus DAT und speichern sie in deine Akte.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="dat-contract-id">DAT-Vorgangsnummer</Label>
            <Input
              id="dat-contract-id"
              type="text"
              inputMode="numeric"
              pattern="\d*"
              placeholder="z.B. 122575735"
              value={contractId}
              onChange={(e) => setContractId(e.target.value.replace(/[^\d]/g, ""))}
              autoFocus
              disabled={loading}
              data-testid="dat-contract-id-input"
              className="font-mono"
            />
            <p className="text-[11px] text-zinc-500">
              Die Vorgangsnummer findest du in SilverDAT calculateExpert oben
              rechts im Vorgang oder in der DAT-PDF.
            </p>
          </div>

          {error && (
            <div
              className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800"
              data-testid="dat-contract-error"
            >
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                onClose?.();
              }}
              disabled={loading}
              data-testid="dat-contract-cancel-btn"
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              disabled={loading || !contractId}
              className="brand-gradient-bg text-white"
              data-testid="dat-contract-pull-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Wird abgerufen…
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Daten abrufen
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
